// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FormFieldIcon } from "@bgs/MobiusOverrides/FormField/FormField";
import { ButtonSizeVariants } from "@insite/mobius/Button";
import { FormFieldPresentationPropsCommon } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import get from "@insite/mobius/utilities/get";
import Color from "color";
import { css } from "styled-components";

export interface VariantValues {
    fontSize: number;
    height: number;
    icon: number;
    iconPadding: number;
    descriptionLineSpacing: number;
    borderRadius: number;
    datePickerWidth: number;
    leftLabelPadding: number;
    button: ButtonSizeVariants;
    labelHeight: number;
}

interface SizeVariantValues {
    default: VariantValues;
    small: VariantValues;
}

export const sizeVariantValues: SizeVariantValues = {
    default: {
        fontSize: 14,
        height: 36,
        icon: 14,
        iconPadding: 6,
        descriptionLineSpacing: 6,
        borderRadius: 0,
        datePickerWidth: 145,
        leftLabelPadding: 9,
        button: "medium",
        labelHeight: 32,
    },
    small: {
        fontSize: 14,
        height: 36,
        icon: 14,
        iconPadding: 6,
        descriptionLineSpacing: 6,
        borderRadius: 0,
        datePickerWidth: 145,
        leftLabelPadding: 9,
        button: "medium",
        labelHeight: 32,
    },
};

export const borderTypes = {
    error: {
        color: "colors.danger.main",
        width: 2,
        borderPaddingRule: "padding: 0 10px 0 10px;",
        textAreaBorderPaddingRule: "padding: 10px 10px;",
        underlinePaddingRule: "padding: 0 10px;",
        textAreaUnderlinePaddingRule: "padding: 10px 10px 8px 10px;",
    },
    focus: {
        color: "colors.text.main",
        width: 2,
        borderPaddingRule: "padding: 0 9px 0 9px;",
        textAreaBorderPaddingRule: "padding: 9px 9px;",
        underlinePaddingRule: "padding: 0 10px;",
        textAreaUnderlinePaddingRule: "padding: 10px 10px 8px 10px;",
    },
    inactive: {
        color: "colors.text.main",
        width: 1,
        borderPaddingRule: "padding: 0 10px 0 10px;",
        textAreaBorderPaddingRule: "padding: 10px 10px;",
        underlinePaddingRule: "padding: 0 10px 1px 10px;",
        textAreaUnderlinePaddingRule: "padding: 10px 10px 9px 10px;",
    },
} as const;

type BorderKind = FormFieldPresentationPropsCommon["border"];

const borderByState = (
    {
        border,
        _sizeVariant,
        theme,
    }: {
        border: BorderKind;
        _sizeVariant: keyof typeof sizeVariantValues;
        theme: BaseTheme;
    },
    state: keyof typeof borderTypes,
    textarea = false,
) => {
    const {
        width,
        color,
        borderPaddingRule,
        underlinePaddingRule,
        textAreaBorderPaddingRule,
        textAreaUnderlinePaddingRule,
    } = borderTypes[state];
    let borderStyle = "solid";
    if (state === "focus") {
        borderStyle = get(theme, "focus.style");
    }
    const colorProp = get(theme, color);
    /* Border radius of 0 is included to override macOS select default 5px radius */
    if (border === "rectangle") {
        return `border: ${width}px ${borderStyle} ${colorProp};
        border-radius: ${sizeVariantValues[_sizeVariant].borderRadius}px;
        ${textarea ? textAreaBorderPaddingRule : borderPaddingRule}`;
    }
    if (border === "rounded") {
        return `border: ${width}px ${borderStyle} ${colorProp};
        border-radius: ${sizeVariantValues[_sizeVariant].borderRadius}px;
        ${textarea ? textAreaBorderPaddingRule : borderPaddingRule}`;
    }
    return `border-style: ${borderStyle};
    border-width: 0 0 ${width}px 0;
    border-color: ${colorProp};
    ${textarea ? textAreaUnderlinePaddingRule : underlinePaddingRule}
    border-radius: 0;`;
};

export const disabledByType = ({ border, theme }: { border: BorderKind; theme: BaseTheme }) => {
    if (border === "rectangle" || border === "rounded") {
        const disabledBkg = Color(get(theme, "colors.common.disabled"))
            .rgb()
            .mix(Color(get(theme, "colors.common.background")), 0.8)
            .toString();
        return css`
            background: ${disabledBkg};
            color: ${get(theme, "colors.common.disabled")};
            & + ${/* sc-selector */ FormFieldIcon}, & + button ${/* sc-selector */ FormFieldIcon} {
                background: ${disabledBkg};
            }
        `;
    }
    return "border-bottom-style: dotted;";
};

export default borderByState;
